import { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, UserPlus, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "../../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium outline-none transition focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20";

export const UsersTab = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () =>
    axios
      .get(`${API}/users`, { withCredentials: true })
      .then((r) => setUsers(r.data))
      .catch(() => setUsers([]));

  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/users`, form, { withCredentials: true });
      toast.success(`${form.email} can now sign in to the admin dashboard.`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove admin access for ${u.email}?`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`, { withCredentials: true });
      toast.success(`${u.email} removed.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-users-tab">
      <div className="rounded-3xl bg-white border border-slate-100 p-6 sm:p-8 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]">
        <h3 className="font-display font-bold text-lg text-[#0b132b]">Team Accounts</h3>
        <p className="mt-1 text-sm text-slate-500">Everyone here has full admin access.</p>
        <div className="mt-5 space-y-3" data-testid="users-list">
          {users === null ? (
            <div className="w-8 h-8 rounded-full border-4 border-[var(--brand)]/20 border-t-[var(--brand)] animate-spin" />
          ) : (
            users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 rounded-2xl border border-slate-100 p-4" data-testid={`user-row-${u.id}`}>
                <span className="w-10 h-10 rounded-full bg-[var(--brand-soft)] flex items-center justify-center font-display font-bold text-[var(--brand)]">
                  {u.name?.[0]?.toUpperCase() || "?"}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-[#0b132b] truncate">{u.name}</p>
                  <p className="text-xs text-slate-400 truncate">{u.email} · {u.role}</p>
                </div>
                {me && u.id !== me.id && (
                  <button
                    data-testid={`user-delete-${u.id}`}
                    onClick={() => remove(u)}
                    className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                    aria-label={`Remove ${u.email}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-slate-100 p-6 sm:p-8 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)] h-fit">
        <h3 className="font-display font-bold text-lg text-[#0b132b] flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[var(--brand)]" /> Add Staff Account
        </h3>
        <p className="mt-1 text-sm text-slate-500">They'll sign in at /login and land straight in this dashboard.</p>
        <form onSubmit={add} className="mt-5 space-y-4" data-testid="users-add-form">
          <input data-testid="users-name-input" value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
          <input data-testid="users-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={inputCls} />
          <input data-testid="users-password-input" type="text" value={form.password} onChange={set("password")} placeholder="Temporary password (min 6 chars)" className={inputCls} />
          <button
            data-testid="users-add-button"
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-[var(--brand)] text-white text-sm font-bold py-3 shadow-lg shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] disabled:opacity-60"
          >
            {busy ? "Adding…" : "Add Account"}
          </button>
        </form>
        <p className="mt-4 text-xs text-slate-400 flex items-start gap-2">
          <UsersIcon className="w-4 h-4 shrink-0 mt-0.5" />
          Staff can change their own password later via the "Forgot password" link on the login page.
        </p>
      </div>
    </div>
  );
};
